import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Brain, TrendingUp, TrendingDown, Minus, Target, Award, Zap, BookOpen, Loader2, ArrowUp, ArrowDown, AlertTriangle, CheckCircle, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProgressAnalytics = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/student/ai-progress-report`);
      setReport(res.data);
      setGenerated(true);
    } catch { toast.error('Failed to load progress data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /><p className="ml-3 text-gray-500">Analyzing your progress...</p></div>;

  if (!report) return (
    <Card className="p-12 text-center"><BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">Start taking quizzes to see your progress analytics!</p></Card>
  );

  const { chart_data, overall_average, total_activities, ai_analysis } = report;
  const trendIcon = (t) => t === 'up' ? <ArrowUp className="w-4 h-4 text-green-500" /> : t === 'down' ? <ArrowDown className="w-4 h-4 text-red-500" /> : <Minus className="w-4 h-4 text-gray-400" />;
  const trendColor = (t) => t === 'up' ? 'text-green-600' : t === 'down' ? 'text-red-600' : 'text-gray-500';

  return (
    <div className="space-y-6" data-testid="progress-analytics">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200">
          <p className="text-xs text-teal-600 font-medium">Overall Average</p>
          <p className="text-3xl font-bold text-teal-700" data-testid="overall-avg">{overall_average}%</p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
          <p className="text-xs text-indigo-600 font-medium">Subjects</p>
          <p className="text-3xl font-bold text-indigo-700">{report.subject_count || 0}</p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <p className="text-xs text-amber-600 font-medium">Activities</p>
          <p className="text-3xl font-bold text-amber-700">{total_activities}</p>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <p className="text-xs text-green-600 font-medium">Trajectory</p>
          <p className="text-lg font-bold text-green-700 capitalize flex items-center gap-1">
            {ai_analysis?.predicted_trajectory === 'improving' ? <TrendingUp className="w-5 h-5" /> : ai_analysis?.predicted_trajectory === 'needs_attention' ? <AlertTriangle className="w-5 h-5 text-amber-500" /> : <Minus className="w-5 h-5" />}
            {ai_analysis?.predicted_trajectory?.replace('_',' ') || 'N/A'}
          </p>
        </Card>
      </div>

      {/* Subject Progress Chart */}
      {chart_data?.length > 0 && (
        <Card className="p-6 bg-white/90 border-0 shadow-lg">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-teal-600" /> Subject Performance</h3>
          <div className="space-y-4">
            {chart_data.map((subj, idx) => (
              <div key={idx} className="space-y-2" data-testid={`subject-${subj.subject}`}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm capitalize">{subj.subject}</span>
                    <Badge variant="outline" className="text-xs">{subj.total_attempts} attempts</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {trendIcon(subj.trend)}
                    <span className={`text-sm font-semibold ${trendColor(subj.trend)}`}>{subj.recent_avg}%</span>
                  </div>
                </div>
                <div className="relative">
                  <Progress value={subj.recent_avg} className="h-3 rounded-full" />
                  {subj.early_avg !== subj.recent_avg && (
                    <div className="absolute top-0 h-3 border-r-2 border-dashed border-gray-400" style={{ left: `${Math.min(subj.early_avg, 100)}%` }} title={`Previous: ${subj.early_avg}%`} />
                  )}
                </div>
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Previous avg: {subj.early_avg}%</span>
                  <span className={trendColor(subj.trend)}>
                    {subj.trend === 'up' ? `+${(subj.recent_avg - subj.early_avg).toFixed(1)}%` : subj.trend === 'down' ? `${(subj.recent_avg - subj.early_avg).toFixed(1)}%` : 'No change'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* AI Analysis */}
      {ai_analysis ? (
        <div className="space-y-4">
          {/* Overall Assessment */}
          <Card className={`p-5 border-2 ${ai_analysis.grade_level === 'performing_above' ? 'bg-green-50 border-green-300' : ai_analysis.grade_level === 'below_grade' ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-300'}`}>
            <div className="flex items-start gap-3">
              <Brain className="w-6 h-6 text-indigo-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-sm mb-1">AI Assessment</h4>
                <p className="text-sm text-gray-700" data-testid="ai-assessment">{ai_analysis.overall_assessment}</p>
                {ai_analysis.motivational_message && <p className="text-sm text-indigo-700 mt-2 italic font-medium">"{ai_analysis.motivational_message}"</p>}
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Strongest Subjects */}
            {ai_analysis.strongest_subjects?.length > 0 && (
              <Card className="p-4 border-green-100">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-green-700"><Star className="w-4 h-4" /> Strongest Subjects</h4>
                <div className="flex flex-wrap gap-2">{ai_analysis.strongest_subjects.map((s, i) => <Badge key={i} className="bg-green-100 text-green-700 capitalize">{s}</Badge>)}</div>
              </Card>
            )}
            {/* Weakest Subjects */}
            {ai_analysis.weakest_subjects?.length > 0 && (
              <Card className="p-4 border-amber-100">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-amber-700"><Target className="w-4 h-4" /> Needs Attention</h4>
                <div className="flex flex-wrap gap-2">{ai_analysis.weakest_subjects.map((s, i) => <Badge key={i} className="bg-amber-100 text-amber-700 capitalize">{s}</Badge>)}</div>
              </Card>
            )}
          </div>

          {/* Areas of Focus */}
          {ai_analysis.areas_of_focus?.length > 0 && (
            <Card className="p-4">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-red-500" /> Areas of Focus</h4>
              <div className="space-y-2">{ai_analysis.areas_of_focus.map((a, i) => (
                <div key={i} className="p-3 bg-red-50 rounded-lg border border-red-100">
                  <p className="text-sm font-medium text-red-800 capitalize">{a.subject}</p>
                  <p className="text-xs text-red-600">{a.reason}</p>
                  {a.specific_topic && <p className="text-xs text-gray-500 mt-1">Focus on: {a.specific_topic}</p>}
                </div>
              ))}</div>
            </Card>
          )}

          {/* Improvements */}
          {ai_analysis.improvements?.length > 0 && (
            <Card className="p-4">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-500" /> Recent Improvements</h4>
              <div className="space-y-2">{ai_analysis.improvements.map((imp, i) => (
                <div key={i} className="flex items-start gap-2 p-2 bg-green-50 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div><p className="text-sm">{imp.area}</p>{imp.evidence && <p className="text-xs text-gray-500">{imp.evidence}</p>}</div>
                </div>
              ))}</div>
            </Card>
          )}

          {/* Recommendations */}
          {ai_analysis.recommendations?.length > 0 && (
            <Card className="p-4 bg-indigo-50 border-indigo-200">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2 text-indigo-700"><Zap className="w-4 h-4" /> Recommendations</h4>
              <div className="space-y-2">{ai_analysis.recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-3 p-2 bg-white rounded-lg border">
                  <Badge className={`text-xs flex-shrink-0 mt-0.5 ${rec.priority === 'high' ? 'bg-red-100 text-red-700' : rec.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{rec.priority}</Badge>
                  <div><p className="text-sm">{rec.recommendation}</p><Badge variant="outline" className="text-xs mt-1 capitalize">{rec.category?.replace('_',' ')}</Badge></div>
                </div>
              ))}</div>
            </Card>
          )}
        </div>
      ) : !loading && generated && (
        <Card className="p-6 text-center">
          <Brain className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Take more quizzes and activities to unlock AI progress analysis</p>
        </Card>
      )}
    </div>
  );
};

export default ProgressAnalytics;
